import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

/**
 * SubjectModal
 * ------------
 * Requires `framer-motion` (npm install framer-motion) if not already installed.
 * Shares its design tokens with StudentAssignmentPage — worth lifting PALETTE/FONT
 * into a single `theme.js` once you have a couple more components using them.
 */

const PALETTE = {
  ink: "#1B2333",
  inkSoft: "#5B6478",
  canvas: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E5E8F0",
  primary: "#3A4DE0",
  primarySoft: "#EEF0FD",
  amber: "#C97F1B",
  amberSoft: "#FBF1E1",
};

const FONT = {
  display: "'Sora', 'Segoe UI', sans-serif",
  body: "'Inter', 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', 'Courier New', monospace",
};

const ActivityOption = ({
  icon,
  title,
  description,
  tint,
  tintSoft,
  onClick,
  delay,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay, ease: "easeOut" }}
  >
    <Box
      component={motion.button}
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.985 }}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2.25,
        borderRadius: "14px",
        border: `1px solid ${PALETTE.border}`,
        bgcolor: PALETTE.surface,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          borderColor: tint,
          boxShadow: `0 6px 16px ${tint}1F`,
        },
        "&:focus-visible": {
          outline: `2px solid ${tint}`,
          outlineOffset: "2px",
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: tintSoft,
          color: tint,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: FONT.display,
            fontWeight: 700,
            fontSize: 15,
            color: PALETTE.ink,
            mb: 0.25,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: FONT.body,
            fontSize: 12.5,
            color: PALETTE.inkSoft,
            lineHeight: 1.45,
          }}
        >
          {description}
        </Typography>
      </Box>

      <ChevronRightIcon
        sx={{ color: PALETTE.inkSoft, fontSize: 20, flexShrink: 0 }}
      />
    </Box>
  </motion.div>
);

const SubjectModal = ({
  openActivityModal,
  setOpenActivityModal,
  handleAssignmentClick,
  handleExamClick,
}) => {
  const close = () => setOpenActivityModal(false);

  return (
    <Dialog
      open={openActivityModal}
      onClose={close}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: PALETTE.surface,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ p: 3.5, pb: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT.mono,
                fontSize: 11,
                fontWeight: 600,
                color: PALETTE.primary,
                letterSpacing: "0.08em",
                mb: 0.5,
              }}
            >
              NEW ACTIVITY
            </Typography>
            <Typography
              sx={{
                fontFamily: FONT.display,
                fontWeight: 700,
                fontSize: 19,
                color: PALETTE.ink,
                lineHeight: 1.3,
              }}
            >
              What are you creating?
            </Typography>
          </Box>

          <IconButton
            onClick={close}
            size="small"
            aria-label="Close"
            sx={{
              color: PALETTE.inkSoft,
              mt: -0.5,
              mr: -0.5,
              "&:hover": { bgcolor: PALETTE.canvas },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3.5, pt: 1.5 }}>
        <Typography
          sx={{
            fontFamily: FONT.body,
            fontSize: 13,
            color: PALETTE.inkSoft,
            mb: 2.5,
          }}
        >
          Pick a format to set up for this subject.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <ActivityOption
            icon={<QuizOutlinedIcon sx={{ fontSize: 22 }} />}
            title="Exam"
            description="A timed, structured assessment for grading."
            tint={PALETTE.primary}
            tintSoft={PALETTE.primarySoft}
            onClick={handleExamClick}
            delay={0.05}
          />
          <ActivityOption
            icon={<AssignmentOutlinedIcon sx={{ fontSize: 22 }} />}
            title="Assignment"
            description="An open-ended task students complete and submit."
            tint={PALETTE.amber}
            tintSoft={PALETTE.amberSoft}
            onClick={handleAssignmentClick}
            delay={0.12}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubjectModal;
